import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { MAX_CAMPAIGN_BRANDS, NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { forwardRef, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BentoBrand, SearchParams } from "schemas/dashboard";
import { routes } from "schemas/routes";

import CampaignBrandCard from "./CampaignBrandCard";
import styles from "./styles";

interface Props {
  brandsLoading: boolean;
  fetchMoreLoading: boolean;
  brands: BentoBrand[];
  numberSelected: number;
  ref: any;
  total: number;
  isRepitching?: boolean;
}
const REF_INDEX = 9; // The index used to start loading more recommendations

export default forwardRef<HTMLDivElement, Props>(
  (
    {
      brandsLoading,
      fetchMoreLoading,
      numberSelected,
      brands,
      total,
      isRepitching = false,
    },
    ref,
  ) => {
    const [searchParams] = useSearchParams();
    const { selectAll, setSelectAll, selectedBrandIds, setSelectedBrandIds } =
      useContext(NewCampaignContext);
    const { subscription } = useContext(SubscriptionContext);

    const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);
    const isSearching = brandQuery && brandQuery?.length > 0;
    const { excludedBentoSearchFilterIds, searchFilters } = useContext(
      OrganizationUserContext,
    );

    const isExcludedKeyWords = () => {
      if (!excludedBentoSearchFilterIds) return false;
      for (const excludedFilterId of excludedBentoSearchFilterIds) {
        const matchedFilter = searchFilters?.find(
          (f) => f.id === excludedFilterId,
        );
        if (matchedFilter) {
          const inMatchedFilter = matchedFilter?.keywords?.find(
            (x) => x === brandQuery?.toLowerCase(),
          );
          if (inMatchedFilter) return true;
        }
      }
      return false;
    };

    const handleSelect = (bentoBrand: BentoBrand) => {
      setSelectAll(false);
      setSelectedBrandIds((prev) => {
        const copy = new Set(prev);
        if (copy.has(bentoBrand.id)) {
          copy.delete(bentoBrand.id);
        } else {
          copy.add(bentoBrand.id);
        }

        return copy;
      });
    };

    const isSelected = (bentoBrand: BentoBrand, idx: number) => {
      if (idx < subscription?.remainingMessages! && selectAll) {
        return true;
      }
      return selectedBrandIds.has(bentoBrand.id);
    };

    const throwWarning = () => {
      if (
        numberSelected >= subscription?.remainingMessages! &&
        subscription?.remainingMessages !== 0
      ) {
        return `You only have ${subscription?.remainingMessages} emails left in this billing cycle`;
      }
      if (numberSelected >= MAX_CAMPAIGN_BRANDS) {
        return `You can only select up to ${MAX_CAMPAIGN_BRANDS} brands for one campaign.`;
      }
      return "";
    };

    return (
      <Grid item container rowGap={1}>
        <Grid item xs={12}>
          <Typography sx={styles.searchingText} variant="h6" textAlign="left">
            {isSearching && `Searching "${brandQuery}"`}
          </Typography>
        </Grid>

        {!brandsLoading && !fetchMoreLoading && brands.length === 0 && (
          <Grid container item xs={12} rowGap={2}>
            {isExcludedKeyWords() ? (
              <Grid sx={styles.excludedContainer}>
                <Typography sx={{ fontSize: 16, mb: 2 }}>
                  No results found.
                </Typography>
                It appears you have excluded the keyword{" "}
                <strong>
                  <em>{brandQuery}</em>
                </strong>{" "}
                from your recommendation preferences. To change this, go to your{" "}
                <Link to={`/${routes.profile}`}>Profile page</Link> and update
                the excluded keyword filters at the{" "}
                <strong>Let us tailor your recommendations</strong> section.
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  No results found {isExcludedKeyWords() && "True"}
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  Try changing the filters above to see more results.
                </Grid>
              </>
            )}
          </Grid>
        )}

        {brandsLoading && (
          <Grid
            container
            item
            xs={12}
            justifyContent={"center"}
            sx={styles.skeletonContainer}
          >
            {Array.from({ length: 20 }, (x, i) => (
              <Skeleton
                sx={{ mb: 2 }}
                key={i}
                variant="rounded"
                width={"100%"}
                height={100}
              />
            ))}
          </Grid>
        )}

        {!brandsLoading && brands.length !== 0 && (
          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: 1, mt: -1 }}
          >
            {total !== 0 && numberSelected === 0 && (
              <Typography color="textSecondary" variant="caption">
                {total === 10000 ? "10,000+" : total} results
              </Typography>
            )}
            {numberSelected > 0 && (
              <Tooltip enterTouchDelay={0} title={throwWarning()}>
                <Grid item xs="auto">
                  {throwWarning() && (
                    <Box
                      component="span"
                      className="fa-solid fa-triangle-exclamation"
                      sx={{ color: "warning.main", mr: 0.5 }}
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {numberSelected} Selected
                  </Typography>
                </Grid>
              </Tooltip>
            )}
            <Grid item xs="auto">
              <FormControlLabel
                sx={{ mr: 2 }}
                control={
                  <Checkbox
                    onChange={() => {
                      setSelectAll(!selectAll);
                      setSelectedBrandIds(new Set());
                    }}
                    checked={selectAll}
                    disableRipple
                    icon={
                      <Box
                        sx={{ color: "green.main" }}
                        component="i"
                        className="fa-regular fa-square"
                      />
                    }
                    checkedIcon={
                      <Box
                        component="i"
                        className="fa-solid fa-square-check"
                        sx={{ color: "green.main" }}
                      />
                    }
                  />
                }
                label={<Box sx={{ pr: 1 }}>Select All</Box>}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        )}

        <Grid container item xs={12} justifyContent="center" rowGap={1}>
          {!brandsLoading &&
            brands?.map((brand: BentoBrand, idx: number) => (
              <CampaignBrandCard
                ref={
                  (
                    brands.length > REF_INDEX
                      ? idx === brands.length - REF_INDEX
                      : idx === brands.length - 1
                  )
                    ? ref
                    : undefined
                }
                key={`brand-card-${brand.id}-${idx}`}
                brand={brand}
                actions={
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                      handleSelect(brand);
                    }}
                    disableRipple
                    checked={isSelected(brand, idx)}
                    icon={
                      <Box
                        sx={{ color: "green.main" }}
                        component="i"
                        className="fa-regular fa-square"
                      />
                    }
                    checkedIcon={
                      <Box
                        component="i"
                        className="fa-solid fa-square-check"
                        sx={{ color: "green.main" }}
                      />
                    }
                  />
                }
              />
            ))}
        </Grid>

        {fetchMoreLoading && (
          <Grid
            container
            item
            xs={12}
            justifyContent={"center"}
            sx={styles.skeletonContainer}
          >
            {Array.from({ length: 3 }, (x, i) => (
              <Skeleton
                sx={{ mb: 2 }}
                key={i}
                variant="rounded"
                width={"100%"}
                height={100}
              />
            ))}
          </Grid>
        )}
      </Grid>
    );
  },
);
